import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import Topbar from "@/components/Topbar";
import EditModal from "@/components/EditModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import ManualCopyModal from "@/components/ManualCopyModal";
import FolderSidebar from "@/components/FolderSidebar";
import MoveToFolderModal from "@/components/MoveToFolderModal";
import { toast } from "sonner";
import {
  Plus,
  Link as LinkIcon,
  Pencil,
  Trash2,
  Globe,
  Lock,
  Film,
  Loader2,
  AlertTriangle,
  Sparkles,
  Download,
  HardDrive,
  Trash,
  RefreshCw,
  LifeBuoy,
  Search,
  Folder as FolderIcon,
  CheckSquare,
  Square,
  X,
  Clock,
  Pause,
  Play,
  EyeOff,
  Eye,
} from "lucide-react";

function formatBytes(n) {
  if (n == null) return "-";
  const units = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Milestones the backend writes when the worker is *not yet touching* a
// pending upload — either freshly enqueued or waiting behind a busy job.
// If a video is `pending` and its milestone is one of these AND there is
// no progress yet, we render the grey "Wartet in Warteschlange" badge
// instead of the blue pulsing "Wird für Mobile optimiert…" one, so the
// admin can tell at a glance which upload is actually running.
const WAITING_MILESTONES = new Set([
  null,
  undefined,
  "",
  "attempt_started",
  "queued",
  "reprocess_queued",
  "enqueued",
]);

function isWaitingInQueue(v) {
  if (v.processing_status !== "pending") return false;
  if (
    typeof v.mobile_transcode_progress === "number" &&
    v.mobile_transcode_progress > 0
  ) {
    return false;
  }
  return WAITING_MILESTONES.has(v.processing_milestone);
}

function Thumb({ video }) {
  const [errored, setErrored] = useState(false);
  if (errored || !video.has_thumbnail) {
    return (
      <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[#1f1f23] to-[#0a0a0b]">
        <Film className="text-[#3a3a40]" size={48} />
      </div>
    );
  }
  return (
    <img
      src={`${API_BASE}/videos/${video.slug}/thumbnail`}
      alt=""
      className="absolute inset-0 w-full h-full object-cover"
      onError={() => setErrored(true)}
      loading="lazy"
    />
  );
}

function StorageStatusBanner({ storage, onCleanedUp }) {
  const [cleaning, setCleaning] = useState(false);
  const isS3 = storage.backend === "s3";
  const usedGB = (storage.disk_used_bytes / 1024 ** 3).toFixed(1);
  const totalGB = (storage.disk_total_bytes / 1024 ** 3).toFixed(1);
  const freeGB = (storage.disk_free_bytes / 1024 ** 3).toFixed(1);
  const videosMB = (storage.videos_bytes / 1024 ** 2).toFixed(0);
  const usedPct = Math.round((storage.disk_used_bytes / storage.disk_total_bytes) * 100);
  const lowSpace = storage.disk_free_bytes < 1.5 * 1024 ** 3; // < 1.5 GB
  const ffmpegBroken = !storage.ffmpeg_available;

  const onCleanup = async () => {
    if (cleaning) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/cleanup");
      const freedMB = (data.bytes_freed / 1024 ** 2).toFixed(1);
      const total = data.removed_videos + data.removed_mobiles + data.removed_thumbs;
      toast.success(`${total} Orphan-Datei(en) entfernt — ${freedMB} MB frei`);
      onCleanedUp?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCleaning(false);
    }
  };

  const tone = ffmpegBroken
    ? "bg-red-500/10 border-red-500/30 text-red-200"
    : !isS3 && lowSpace
    ? "bg-amber-500/10 border-amber-500/30 text-amber-200"
    : "bg-[#17171a] border-[#2a2a30] text-[#9a9aa3]";

  return (
    <div
      className={`mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border px-4 py-3 ${tone}`}
      data-testid="admin-storage-banner"
    >
      <div className="flex items-center gap-3 text-sm">
        <HardDrive size={18} className="shrink-0" />
        <div>
          {isS3 ? (
            <div className="font-mono" data-testid="admin-storage-usage">
              Cloud-Speicher: <span className="text-emerald-300">R2</span> · {storage.video_count} Videos · {videosMB} MB · Pod-Cache {usedGB}/{totalGB} GB ({usedPct}%)
            </div>
          ) : (
            <div className="font-mono" data-testid="admin-storage-usage">
              Speicher: {usedGB} / {totalGB} GB belegt ({usedPct}%) · {freeGB} GB frei · {videosMB} MB Videos
            </div>
          )}
          {ffmpegBroken && (
            <div className="mt-1 text-xs">
              <AlertTriangle size={12} className="inline mr-1" />
              ffmpeg/ffprobe fehlt! Uploads schlagen fehl bis behoben.
            </div>
          )}
          {!ffmpegBroken && !isS3 && lowSpace && (
            <div className="mt-1 text-xs">
              <AlertTriangle size={12} className="inline mr-1" />
              Wenig freier Speicher — bitte aufräumen oder Videos löschen.
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onCleanup}
        disabled={cleaning}
        className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-[#2a2a30] bg-[#1f1f23] text-xs font-medium text-white hover:border-[#3a3a40] disabled:opacity-50"
        data-testid="admin-cleanup-button"
        title="Verwaiste Dateien (ohne DB-Eintrag) löschen"
      >
        {cleaning ? <Loader2 size={12} className="animate-spin" /> : <Trash size={12} />}
        Aufräumen
      </button>
    </div>
  );
}


function formatBlockingDetail(detail) {
  if (detail && typeof detail === "object" && Array.isArray(detail.blocking_projects)) {
    const names = detail.blocking_projects
      .map((p) => `„${p.title}"`)
      .slice(0, 3)
      .join(", ");
    const suffix = detail.blocking_projects.length > 3 ? " und weitere" : "";
    const renderedHint =
      Array.isArray(detail.rendered_projects) && detail.rendered_projects.length > 0
        ? ` (${detail.rendered_projects.length} bereits gerendertes Projekt würde nach dem Löschen automatisch entkoppelt.)`
        : "";
    return (
      detail.message ||
      `Video wird in ${names}${suffix} verwendet — bitte erst Projekt anpassen.${renderedHint}`
    );
  }
  return formatApiErrorDetail(detail);
}

export default function Admin() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("original"); // "original" | "edited"
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [manualCopyUrl, setManualCopyUrl] = useState("");
  const [storage, setStorage] = useState(null);
  const [reprocessing, setReprocessing] = useState({}); // slug -> true while POST in flight
  const [pausing, setPausing] = useState({});   // slug -> true while pause-transcode POST is in flight
  const [resuming, setResuming] = useState({}); // slug -> true while resume-transcode POST is in flight
  const [dismissing, setDismissing] = useState({}); // slug -> true while dismiss/undismiss-mobile-error POST is in flight
  const [reprocessAllOpen, setReprocessAllOpen] = useState(false);
  const [reprocessAllBusy, setReprocessAllBusy] = useState(false);
  const [rescueBusy, setRescueBusy] = useState(false);

  // ---- Phase 12: folders + search + bulk-select ------------------------
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("all"); // "all" | "none" | folderId
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState(new Set());
  const [moveTarget, setMoveTarget] = useState(null); // {slug, folder_id} for single, or "bulk" for multi

  // Debounce the search box so we don't spam the backend on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await api.get("/videos", { params });
      setVideos(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const { data } = await api.get("/folders");
      setFolders(data);
    } catch (err) {
      // Non-fatal — sidebar just shows no folders and the user can still
      // upload/edit videos.
      console.warn("loadFolders failed", err?.message);
    }
  };

  const loadStorage = async () => {
    try {
      const { data } = await api.get("/admin/storage-status");
      setStorage(data);
    } catch {
      /* non-critical — banner just stays hidden */
    }
  };

  useEffect(() => {
    load();
    loadStorage();
    loadFolders();
  }, []);

  useEffect(() => {
    // Re-fetch videos when the debounced search term changes.
    load();
  }, [debouncedSearch]);

  const originals = useMemo(() => videos.filter((v) => !v.is_edited), [videos]);
  const edited = useMemo(() => videos.filter((v) => v.is_edited), [videos]);
  const tabScoped = tab === "edited" ? edited : originals;

  // Client-side folder filter — kept in-memory because the folder-count
  // in the sidebar reflects ALL videos (across tabs) and we already fetched
  // the full list. Sidebar counts are recomputed here so they stay live.
  const uncategorizedCount = useMemo(
    () => tabScoped.filter((v) => !v.folder_id).length,
    [tabScoped],
  );

  const visible = useMemo(() => {
    if (selectedFolder === "all") return tabScoped;
    if (selectedFolder === "none") return tabScoped.filter((v) => !v.folder_id);
    return tabScoped.filter((v) => v.folder_id === selectedFolder);
  }, [tabScoped, selectedFolder]);

  // Sidebar folder counts scoped to the current tab, so switching between
  // "Originale" and "Bearbeitete Videos" also updates the numbers.
  const sidebarFolders = useMemo(() => {
    return folders.map((f) => ({
      ...f,
      video_count: tabScoped.filter((v) => v.folder_id === f.id).length,
    }));
  }, [folders, tabScoped]);

  // Queue-position map for the grey "Wartet in Warteschlange (Platz X von N)"
  // badge. Scoped to the whole library — the worker doesn't care which tab
  // the admin is viewing. Order is by updated_at asc (that's when the
  // upload/reprocess flipped to pending), which matches the FIFO order of
  // the asyncio queue closely enough for a rough "you are here" hint.
  const queuePositions = useMemo(() => {
    const waiting = videos
      .filter(isWaitingInQueue)
      .slice()
      .sort((a, b) => (a.updated_at || "").localeCompare(b.updated_at || ""));
    const map = new Map();
    waiting.forEach((v, i) => {
      map.set(v.slug, { pos: i + 1, total: waiting.length });
    });
    return map;
  }, [videos]);

  const onCopyLink = async (slug) => {
    // Share links go through the backend /api/share/{slug} social-preview
    // landing page so WhatsApp/iMessage/Facebook see the correct OG tags
    // (which the SPA static build cannot inject on its own in production).
    // Real users are meta-refreshed straight to /v/{slug}. Cache-buster
    // keeps WhatsApp from serving stale preview cards.
    const url = `${window.location.origin}/api/share/${slug}?t=${Date.now()}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      toast.success("Link kopiert");
    } else {
      setManualCopyUrl(url);
    }
  };

  const onConfirmDelete = async () => {
    if (!confirming) return;
    try {
      await api.delete(`/videos/${confirming.slug}`);
      toast.success("Video gelöscht");
      setConfirming(null);
      load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.error(formatBlockingDetail(detail));
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    }
  };

  const onReprocess = async (slug) => {
    if (reprocessing[slug]) return;
    setReprocessing((s) => ({ ...s, [slug]: true }));
    try {
      await api.post(`/admin/videos/${slug}/reprocess`);
      toast.success("Verarbeitung neu gestartet");
      // Optimistically flip local state to pending so the „Wird optimiert..."
      // badge appears immediately, then re-fetch the authoritative list.
      setVideos((prev) =>
        prev.map((v) =>
          v.slug === slug
            ? { ...v, processing_status: "pending", mobile_ready: false, processing_error: null }
            : v,
        ),
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Neu-Verarbeitung fehlgeschlagen",
      );
    } finally {
      setReprocessing((s) => {
        const next = { ...s };
        delete next[slug];
        return next;
      });
    }
  };

  // Phase 13 emergency brake — hits POST /api/admin/videos/{slug}/pause-transcode.
  // Backend sets mobile_transcode_paused=true, processing_attempts=99 and
  // processing_status=ready so both the worker and the reconciler stop touching
  // this video. Reversible via onResumeTranscode.
  const onPauseTranscode = async (slug) => {
    if (pausing[slug]) return;
    setPausing((s) => ({ ...s, [slug]: true }));
    try {
      await api.post(`/admin/videos/${slug}/pause-transcode`);
      toast.success("Notbremse gezogen — Verarbeitung gestoppt");
      // Optimistically flip local state so the „⏸ Pausiert (Notbremse)" badge
      // appears immediately, then re-fetch the authoritative list.
      setVideos((prev) =>
        prev.map((v) =>
          v.slug === slug
            ? {
                ...v,
                mobile_transcode_paused: true,
                mobile_transcode_status: "paused_awaiting_fix",
                processing_status: "ready",
                processing_attempts: 99,
              }
            : v,
        ),
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Notbremse fehlgeschlagen",
      );
    } finally {
      setPausing((s) => {
        const next = { ...s };
        delete next[slug];
        return next;
      });
    }
  };

  // Phase 13 counterpart — hits POST /api/admin/videos/{slug}/resume-transcode.
  // Clears the pause flag, resets attempts, flips status back to pending and
  // re-enqueues the video for the worker.
  const onResumeTranscode = async (slug) => {
    if (resuming[slug]) return;
    setResuming((s) => ({ ...s, [slug]: true }));
    try {
      await api.post(`/admin/videos/${slug}/resume-transcode`);
      toast.success("Verarbeitung fortgesetzt");
      setVideos((prev) =>
        prev.map((v) =>
          v.slug === slug
            ? {
                ...v,
                mobile_transcode_paused: false,
                mobile_transcode_status: null,
                processing_status: "pending",
                processing_attempts: 0,
                mobile_ready: false,
              }
            : v,
        ),
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Fortsetzen fehlgeschlagen",
      );
    } finally {
      setResuming((s) => {
        const next = { ...s };
        delete next[slug];
        return next;
      });
    }
  };

  // Phase 13 UI-noise reducer — after the admin has read the "Nur Original"
  // fallback error, they can dismiss the big orange block, collapsing it
  // to a small grey badge. Reversible.
  const onDismissMobileError = async (slug) => {
    if (dismissing[slug]) return;
    setDismissing((s) => ({ ...s, [slug]: true }));
    try {
      await api.post(`/admin/videos/${slug}/dismiss-mobile-error`);
      toast.success("Fehler ausgeblendet");
      setVideos((prev) =>
        prev.map((v) =>
          v.slug === slug ? { ...v, mobile_error_dismissed: true } : v,
        ),
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Ausblenden fehlgeschlagen",
      );
    } finally {
      setDismissing((s) => {
        const next = { ...s };
        delete next[slug];
        return next;
      });
    }
  };

  const onUndismissMobileError = async (slug) => {
    if (dismissing[slug]) return;
    setDismissing((s) => ({ ...s, [slug]: true }));
    try {
      await api.post(`/admin/videos/${slug}/undismiss-mobile-error`);
      toast.success("Fehler wieder eingeblendet");
      setVideos((prev) =>
        prev.map((v) =>
          v.slug === slug ? { ...v, mobile_error_dismissed: false } : v,
        ),
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Einblenden fehlgeschlagen",
      );
    } finally {
      setDismissing((s) => {
        const next = { ...s };
        delete next[slug];
        return next;
      });
    }
  };

  const onRescueStuck = async () => {
    if (rescueBusy) return;
    setRescueBusy(true);
    try {
      const { data } = await api.post(`/admin/videos/rescue-stuck`);
      const n = data?.rescued ?? 0;
      if (n === 0) {
        toast.info("Keine steckengebliebenen Videos gefunden.");
      } else {
        toast.success(
          `${n} ${n === 1 ? "Video" : "Videos"} wieder in Verarbeitung gestellt`,
        );
        load();
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Fehler beim Retten. Bitte später erneut versuchen.",
      );
    } finally {
      setRescueBusy(false);
    }
  };

  const onReprocessAll = async () => {
    setReprocessAllBusy(true);
    try {
      const { data } = await api.post(`/admin/videos/reprocess-all`);
      const queued = data?.queued ?? 0;
      const busy = data?.already_processing ?? 0;
      if (queued === 0 && busy === 0) {
        toast.info("Keine Videos zum Neuverarbeiten gefunden.");
      } else {
        const busyPart = busy > 0 ? ` (${busy} laufen bereits, übersprungen)` : "";
        toast.success(`${queued} ${queued === 1 ? "Video" : "Videos"} in die Verarbeitungs-Warteschlange gestellt${busyPart}`);
      }
      // Reflect the batch flip in the UI immediately so the „Wird optimiert..."
      // badges appear before the poll cycles catch up.
      setVideos((prev) =>
        prev.map((v) =>
          v.is_edited || v.processing_status === "processing"
            ? v
            : { ...v, processing_status: "pending", mobile_ready: false, processing_error: null },
        ),
      );
      setReprocessAllOpen(false);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Batch-Neuverarbeitung fehlgeschlagen",
      );
    } finally {
      setReprocessAllBusy(false);
    }
  };

  // ---- Phase 12: folder + bulk handlers -------------------------------
  const onCreateFolder = async (name, color) => {
    const { data } = await api.post("/folders", { name, color });
    setFolders((prev) => [...prev, data].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)));
    toast.success(`Ordner „${name}" erstellt`);
    return data;
  };

  const onRenameFolder = async (id, name) => {
    try {
      const { data } = await api.patch(`/folders/${id}`, { name });
      setFolders((prev) => prev.map((f) => (f.id === id ? data : f)));
      toast.success("Ordner umbenannt");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onRecolorFolder = async (id, color) => {
    try {
      const { data } = await api.patch(`/folders/${id}`, { color });
      setFolders((prev) => prev.map((f) => (f.id === id ? data : f)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onDeleteFolder = async (id) => {
    try {
      const { data } = await api.delete(`/folders/${id}`);
      setFolders((prev) => prev.filter((f) => f.id !== id));
      if (selectedFolder === id) setSelectedFolder("all");
      const n = data?.videos_moved ?? 0;
      toast.success(
        n > 0
          ? `Ordner gelöscht — ${n} ${n === 1 ? "Video" : "Videos"} nach „Ohne Ordner" verschoben`
          : "Ordner gelöscht",
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onMoveSingle = async (folderId) => {
    if (!moveTarget || moveTarget === "bulk") return;
    const { slug } = moveTarget;
    try {
      const body = folderId === null ? { folder_clear: true } : { folder_id: folderId };
      await api.patch(`/videos/${slug}`, body);
      toast.success(folderId === null ? "Video aus Ordner entfernt" : "Video verschoben");
      setMoveTarget(null);
      load();
      loadFolders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const onMoveBulk = async (folderId) => {
    const slugs = Array.from(selectedSlugs);
    if (slugs.length === 0) return;
    try {
      const { data } = await api.post("/videos/bulk-move", { slugs, folder_id: folderId });
      toast.success(`${data.moved} ${data.moved === 1 ? "Video" : "Videos"} verschoben`);
      setMoveTarget(null);
      setSelectedSlugs(new Set());
      setSelectMode(false);
      load();
      loadFolders();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleSelectSlug = (slug) => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedSlugs(new Set());
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <Topbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-heading">
              Deine Videos
            </h1>
            <p className="text-sm text-[#9a9aa3] mt-1.5">
              {loading
                ? "Lädt..."
                : `${originals.length} ${originals.length === 1 ? "Original" : "Originale"} · ${edited.length} ${edited.length === 1 ? "Bearbeitet" : "Bearbeitete"}`}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={() => setReprocessAllOpen(true)}
              data-testid="library-reprocess-all-btn"
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl border border-[#2a2a30] bg-[#17171a] text-amber-300 hover:text-amber-200 hover:bg-[#1f1f23] font-medium transition-colors"
              title="Alle Videos für Mobile mit neuer Bitrate neu verarbeiten"
            >
              <RefreshCw size={15} />
              Alle neu verarbeiten
            </button>
            <button
              onClick={onRescueStuck}
              disabled={rescueBusy}
              data-testid="admin-rescue-stuck-btn"
              className="inline-flex items-center justify-center gap-2 h-10 px-3 rounded-xl border border-[#2a2a30] bg-[#17171a] text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Steckengebliebene Videos (Status „Wird optimiert…&quot; seit über 60 Min) sofort neu verarbeiten"
              aria-label="Steckengebliebene Videos retten"
            >
              {rescueBusy ? <Loader2 size={15} className="animate-spin" /> : <LifeBuoy size={15} />}
              <span className="hidden sm:inline">Retten</span>
            </button>
            <button
              onClick={() => navigate("/admin/upload")}
              data-testid="admin-upload-button"
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold shadow-[0_8px_24px_rgba(255,75,80,0.3)] hover:shadow-[0_10px_30px_rgba(255,75,80,0.45)] transition-shadow"
            >
              <Plus size={16} />
              Neues Video hochladen
            </button>
          </div>
        </div>

        {/* Storage status banner — Phase 7 W3 */}
        {storage && <StorageStatusBanner storage={storage} onCleanedUp={() => { load(); loadStorage(); }} /> }

        {/* Phase 12 layout: Sidebar (folders) + main content */}
        <div className="flex flex-col lg:flex-row gap-6">
          <FolderSidebar
            folders={sidebarFolders}
            selectedFolder={selectedFolder}
            totalCount={tabScoped.length}
            uncategorizedCount={uncategorizedCount}
            onSelect={setSelectedFolder}
            onCreateFolder={onCreateFolder}
            onRenameFolder={onRenameFolder}
            onRecolorFolder={onRecolorFolder}
            onDeleteFolder={onDeleteFolder}
          />

          <div className="flex-1 min-w-0">

        {/* Search + bulk-select bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5a60]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Videos suchen…"
              className="w-full h-10 pl-9 pr-9 rounded-xl bg-[#0d0d0e] border border-[#2a2a30] text-sm text-white placeholder-[#5a5a60] outline-none focus:border-[#3a3a40]"
              data-testid="admin-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded inline-flex items-center justify-center text-[#9a9aa3] hover:text-white hover:bg-[#1f1f23]"
                aria-label="Suche leeren"
                data-testid="admin-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              if (selectMode) exitSelectMode();
              else setSelectMode(true);
            }}
            className={`inline-flex items-center justify-center gap-2 h-10 px-3 rounded-xl border text-sm font-medium transition-colors ${
              selectMode
                ? "border-[#ff6b35] bg-[#ff6b35]/10 text-[#ff6b35]"
                : "border-[#2a2a30] bg-[#17171a] text-[#c8c8d0] hover:text-white"
            }`}
            data-testid="admin-select-mode-toggle"
          >
            {selectMode ? <CheckSquare size={14} /> : <Square size={14} />}
            {selectMode ? "Auswahl beenden" : "Auswählen"}
          </button>
        </div>

        {selectMode && selectedSlugs.size > 0 && (
          <div
            className="flex items-center justify-between gap-3 mb-4 px-4 h-11 rounded-xl border border-[#ff6b35]/40 bg-[#ff6b35]/10"
            data-testid="admin-bulk-bar"
          >
            <span className="text-sm text-white">
              {selectedSlugs.size} {selectedSlugs.size === 1 ? "Video" : "Videos"} ausgewählt
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMoveTarget("bulk")}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#ff6b35] hover:bg-[#ff7d4c] px-3 h-8 rounded-md"
                data-testid="admin-bulk-move-btn"
              >
                <FolderIcon size={13} /> In Ordner verschieben
              </button>
              <button
                type="button"
                onClick={exitSelectMode}
                className="text-xs text-[#c8c8d0] hover:text-white px-2 h-8 rounded-md"
                data-testid="admin-bulk-cancel"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div
          className="inline-flex items-center gap-1 mb-6 p-1 rounded-xl border border-[#2a2a30] bg-[#0d0d0e]"
          data-testid="admin-tabs"
          role="tablist"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "original"}
            onClick={() => setTab("original")}
            data-testid="admin-tab-original"
            className={`inline-flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-lg transition-colors ${
              tab === "original"
                ? "bg-[#1f1f23] text-white"
                : "text-[#9a9aa3] hover:text-white"
            }`}
          >
            <Film size={14} /> Originale
            <span className="text-[10px] text-[#5a5a60] tabular-nums ml-1">{originals.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "edited"}
            onClick={() => setTab("edited")}
            data-testid="admin-tab-edited"
            className={`inline-flex items-center gap-2 px-4 h-9 text-sm font-medium rounded-lg transition-colors ${
              tab === "edited"
                ? "bg-[#1f1f23] text-white"
                : "text-[#9a9aa3] hover:text-white"
            }`}
          >
            <Sparkles size={14} /> Bearbeitete Videos
            <span className="text-[10px] text-[#5a5a60] tabular-nums ml-1">{edited.length}</span>
          </button>
        </div>

        {!loading && visible.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-[#2a2a30] bg-[#17171a]/40 p-10 text-center"
            data-testid="admin-videos-empty"
          >
            <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-[#1f1f23] mb-4">
              <Film size={26} className="text-[#9a9aa3]" />
            </div>
            <h2 className="text-lg font-semibold">
              {tab === "edited" ? "Noch keine bearbeiteten Videos" : "Noch keine Videos"}
            </h2>
            <p className="text-sm text-[#9a9aa3] mt-1.5 max-w-md mx-auto">
              {tab === "edited"
                ? "Sobald du im Editor ein Projekt renderst, erscheint das Ergebnis hier."
                : "Lade dein erstes equirectangulares 360°-Video hoch und teile den Link mit deinen Kunden."}
            </p>
            {tab === "edited" ? (
              <Link
                to="/admin/editor"
                className="mt-6 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold"
                data-testid="admin-empty-cta-editor"
              >
                <Sparkles size={16} /> Zum Editor
              </Link>
            ) : (
              <button
                onClick={() => navigate("/admin/upload")}
                className="mt-6 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white font-semibold"
                data-testid="admin-empty-cta"
              >
                <Plus size={16} />
                Erstes Video hochladen
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-videos-grid">
          {visible.map((v) => {
            const isSelected = selectedSlugs.has(v.slug);
            const thumbClickHandler = selectMode
              ? (e) => {
                  e.preventDefault();
                  toggleSelectSlug(v.slug);
                }
              : undefined;
            return (
            <article
              key={v.id}
              data-testid={`admin-video-card-${v.slug}`}
              className={`group rounded-2xl border overflow-hidden transition-all ${
                isSelected
                  ? "border-[#ff6b35] bg-[#1f1417] shadow-[0_12px_40px_-12px_rgba(255,107,53,0.5)]"
                  : "border-[#2a2a30] bg-[#17171a] hover:border-[#3a3a40] hover:shadow-[0_12px_40px_-12px_rgba(255,75,80,0.25)]"
              }`}
            >
              <Link
                to={selectMode ? "#" : `/v/${v.slug}?t=${Date.now()}`}
                onClick={thumbClickHandler}
                className="block relative aspect-video bg-black"
                data-testid={`admin-video-thumb-${v.slug}`}
              >
                <Thumb video={v} />
                {selectMode && (
                  <div
                    className={`absolute top-2 left-2 w-6 h-6 rounded-md border-2 inline-flex items-center justify-center backdrop-blur-sm ${
                      isSelected
                        ? "bg-[#ff6b35] border-[#ff6b35] text-white"
                        : "bg-black/50 border-white/60"
                    }`}
                    data-testid={`admin-select-check-${v.slug}`}
                    aria-label={isSelected ? "Ausgewählt" : "Nicht ausgewählt"}
                  >
                    {isSelected && <CheckSquare size={13} />}
                  </div>
                )}
                {!selectMode && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-black/70 backdrop-blur text-white">
                    {v.visibility === "public" ? (
                      <>
                        <Globe size={11} /> Öffentlich
                      </>
                    ) : (
                      <>
                        <Lock size={11} /> Privat
                      </>
                    )}
                  </span>
                )}
                {v.is_likely_360 && (
                  <span className="absolute top-2 right-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-500/90 text-white">
                    360°
                  </span>
                )}
                {v.is_edited && (
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-[#ff3d6e]/90 text-white">
                    <Sparkles size={11} /> Bearbeitet
                  </span>
                )}
              </Link>
              <div className="p-4">
                <h3 className="font-semibold text-white text-base leading-snug line-clamp-1" title={v.title}>
                  {v.title}
                </h3>
                <div className="mt-1 text-xs text-[#9a9aa3] flex items-center gap-2">
                  <span>{formatDate(v.created_at)}</span>
                  <span className="text-[#3a3a40]">·</span>
                  <span>{formatBytes(v.size)}</span>
                  {v.width && v.height && (
                    <>
                      <span className="text-[#3a3a40]">·</span>
                      <span>{v.width}×{v.height}</span>
                    </>
                  )}
                </div>
                {v.mobile_transcode_status === "paused_awaiting_fix" && (
                  <div
                    className="mt-2 flex flex-col gap-1"
                    data-testid={`admin-status-paused-${v.slug}`}
                  >
                    <div
                      className="inline-flex items-center gap-1.5 text-[10px] font-medium text-[#9a9aa3] bg-[#1f1f23] border border-[#3a3a40] px-2 py-1 rounded-md w-fit"
                      data-testid="mobile-paused-badge"
                    >
                      <Pause size={11} />
                      Pausiert (Notbremse)
                    </div>
                    <div className="text-[10px] text-[#9a9aa3] leading-snug px-1">
                      Verarbeitung manuell gestoppt. Original bleibt streambar. Klicke „Fortsetzen&quot;, um es erneut zu versuchen.
                    </div>
                  </div>
                )}
                {v.mobile_transcode_status !== "paused_awaiting_fix" && (v.processing_status === "pending" || v.processing_status === "processing") && (
                  isWaitingInQueue(v) ? (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-[#9a9aa3] bg-[#1f1f23] border border-[#2a2a30] px-2 py-1 rounded-md"
                      data-testid={`admin-status-waiting-${v.slug}`}
                    >
                      <Clock size={11} />
                      {(() => {
                        const q = queuePositions.get(v.slug);
                        if (q && q.total > 1) {
                          return `Wartet in Warteschlange (Platz ${q.pos} von ${q.total})`;
                        }
                        return "Wartet in Warteschlange";
                      })()}
                    </div>
                  ) : (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-sky-300 bg-sky-500/10 border border-sky-500/30 px-2 py-1 rounded-md"
                      data-testid={`admin-status-pending-${v.slug}`}
                    >
                      <Loader2 size={11} className="animate-spin" />
                      {typeof v.mobile_transcode_progress === "number" && v.mobile_transcode_progress > 0 && v.mobile_transcode_progress < 100
                        ? `Wird für Mobile optimiert (${v.mobile_transcode_progress}%)`
                        : "Wird für Mobile optimiert..."}
                    </div>
                  )
                )}
                {v.processing_status === "failed" && (
                  <div
                    className="mt-2 flex flex-col gap-1"
                    data-testid={`admin-status-failed-${v.slug}`}
                  >
                    <div
                      className="inline-flex items-center gap-1.5 text-[10px] font-medium text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded-md w-fit"
                    >
                      <AlertTriangle size={11} />
                      Mobile-Optimierung fehlgeschlagen
                    </div>
                    {v.processing_error && (
                      <div
                        className="text-[10px] text-amber-200/80 leading-snug break-words px-1"
                        data-testid={`admin-status-failed-detail-${v.slug}`}
                        title={v.processing_error}
                      >
                        {v.processing_error}
                      </div>
                    )}
                  </div>
                )}
                {v.mobile_transcode_status === "failed_giving_up" && v.processing_status === "ready" && (
                  v.mobile_error_dismissed ? (
                    <div
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-[#9a9aa3] bg-[#1f1f23] border border-[#3a3a40] px-2 py-1 rounded-md"
                      data-testid={`admin-status-fallback-dismissed-${v.slug}`}
                    >
                      <span>Nur Original</span>
                      <button
                        onClick={() => onUndismissMobileError(v.slug)}
                        disabled={!!dismissing[v.slug]}
                        className="ml-1 inline-flex items-center gap-1 text-[10px] text-[#6a6a72] hover:text-[#9a9aa3] transition-colors disabled:opacity-60 disabled:cursor-wait"
                        data-testid={`admin-undismiss-mobile-error-${v.slug}`}
                        title="Fehler-Details wieder einblenden"
                      >
                        <Eye size={10} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="mt-2 flex flex-col gap-1"
                      data-testid={`admin-status-fallback-${v.slug}`}
                    >
                      <div
                        className="inline-flex items-center gap-1.5 text-[10px] font-medium text-orange-300 bg-orange-500/10 border border-orange-500/40 px-2 py-1 rounded-md w-fit"
                        data-testid="mobile-fallback-warning"
                      >
                        <AlertTriangle size={11} />
                        Nur Original (Mobile-Optimierung fehlgeschlagen)
                      </div>
                      <div
                        className="text-[10px] text-orange-200/80 leading-snug px-1"
                        data-testid={`admin-status-fallback-detail-${v.slug}`}
                      >
                        <span className="font-medium">Letzter Fehler: </span>
                        <code
                          className="font-mono text-[10px] text-orange-100 break-all"
                          title={v.mobile_transcode_error_last || "kein Detail gespeichert"}
                        >
                          {(() => {
                            const raw = v.mobile_transcode_error_last;
                            if (!raw) return "kein Detail gespeichert";
                            return raw.length > 200 ? raw.slice(0, 200) + "…" : raw;
                          })()}
                        </code>
                      </div>
                      <div className="text-[10px] text-[#9a9aa3] leading-snug px-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>Video spielt in Original-Qualität. Für neuen Versuch: „Neu verarbeiten&quot; klicken.</span>
                        <button
                          onClick={() => onDismissMobileError(v.slug)}
                          disabled={!!dismissing[v.slug]}
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-[#9a9aa3] hover:text-white underline underline-offset-2 decoration-dotted transition-colors disabled:opacity-60 disabled:cursor-wait"
                          data-testid={`admin-dismiss-mobile-error-${v.slug}`}
                          title="Diesen Fehler auf der Kachel ausblenden (jederzeit wieder einblendbar)"
                        >
                          {dismissing[v.slug] ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <EyeOff size={10} />
                          )}
                          Fehler ausblenden
                        </button>
                      </div>
                    </div>
                  )
                )}
                {/* Phase 13: spherical-metadata inject skipped/failed —
                    the mobile.mp4 itself is fine, only the 360° atoms
                    are missing. Player360 still renders via the
                    aspect-ratio fallback. Different banner colour and
                    wording so admins don't confuse this with the full
                    fallback above. */}
                {v.spherical_metadata_status &&
                 v.spherical_metadata_status !== "injected" &&
                 v.spherical_metadata_status !== "source_had_none" &&
                 v.mobile_transcode_status !== "failed_giving_up" &&
                 v.mobile_transcode_status !== "paused_awaiting_fix" &&
                 v.processing_status === "ready" && (
                  <div
                    className="mt-2 flex flex-col gap-1"
                    data-testid={`admin-status-spherical-${v.slug}`}
                  >
                    <div
                      className="inline-flex items-center gap-1.5 text-[10px] font-medium text-sky-300 bg-sky-500/10 border border-sky-500/30 px-2 py-1 rounded-md w-fit"
                      data-testid="spherical-inject-warning"
                    >
                      <AlertTriangle size={11} />
                      {v.spherical_metadata_status === "skipped_disk_space"
                        ? "360°-Atome übersprungen (Ephemeral-Disk zu knapp)"
                        : "360°-Atome nicht injiziert"}
                    </div>
                    {v.spherical_metadata_error && (
                      <div
                        className="text-[10px] text-sky-200/80 leading-snug px-1"
                        data-testid={`admin-status-spherical-detail-${v.slug}`}
                      >
                        <span className="font-medium">Grund: </span>
                        <code
                          className="font-mono text-[10px] text-sky-100 break-all"
                          title={v.spherical_metadata_error}
                        >
                          {(() => {
                            const raw = v.spherical_metadata_error;
                            return raw.length > 220 ? raw.slice(0, 220) + "…" : raw;
                          })()}
                        </code>
                      </div>
                    )}
                    <div className="text-[10px] text-[#9a9aa3] leading-snug px-1">
                      Video spielt trotzdem als 360° (Player-Fallback über Aspect-Ratio).
                    </div>
                  </div>
                )}
                {v.mobile_ready && v.mobile_transcode_status !== "paused_awaiting_fix" && (
                  <div
                    className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-medium text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded-md"
                    data-testid={`admin-status-ready-${v.slug}`}
                  >
                    Mobile bereit
                  </div>
                )}
                {v.is_edited && (
                  <a
                    href={`${API_BASE}/videos/${v.slug}/stream?download=1`}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 h-10 rounded-xl bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e] text-white text-sm font-semibold shadow-[0_6px_18px_rgba(255,75,80,0.3)] hover:shadow-[0_8px_22px_rgba(255,75,80,0.45)] transition-shadow"
                    data-testid={`admin-download-${v.slug}`}
                    download
                  >
                    <Download size={15} />
                    Herunterladen
                  </a>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-x-0.5 gap-y-1">
                  <button
                    onClick={() => onCopyLink(v.slug)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9a9aa3] hover:text-white px-2 py-1.5 rounded-md hover:bg-[#1f1f23] transition-colors"
                    data-testid={`admin-copy-link-${v.slug}`}
                    title="Verwende diesen Button — dann funktioniert die WhatsApp-Vorschau garantiert richtig."
                  >
                    <LinkIcon size={14} />
                    Link
                  </button>
                  <button
                    onClick={() => navigate(`/admin/edit/${v.slug}`)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9a9aa3] hover:text-white px-2 py-1.5 rounded-md hover:bg-[#1f1f23] transition-colors"
                    data-testid={`admin-edit-${v.slug}`}
                    title="Bearbeiten"
                  >
                    <Pencil size={14} />
                    Bearbeiten
                  </button>
                  <button
                    onClick={() => setMoveTarget({ slug: v.slug, folder_id: v.folder_id || null })}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9a9aa3] hover:text-white px-2 py-1.5 rounded-md hover:bg-[#1f1f23] transition-colors"
                    data-testid={`admin-move-${v.slug}`}
                    title={v.folder_id ? "In anderen Ordner verschieben" : "In Ordner verschieben"}
                  >
                    <FolderIcon size={14} />
                    Ordner
                  </button>
                  {!v.is_edited && v.mobile_transcode_status === "paused_awaiting_fix" && (
                    <button
                      onClick={() => onResumeTranscode(v.slug)}
                      disabled={!!resuming[v.slug]}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 px-2 py-1.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-wait"
                      data-testid={`admin-resume-transcode-${v.slug}`}
                      title="Verarbeitung fortsetzen (Notbremse lösen)"
                    >
                      {resuming[v.slug] ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Play size={14} />
                      )}
                      Fortsetzen
                    </button>
                  )}
                  {!v.is_edited && v.mobile_transcode_status !== "paused_awaiting_fix" && (
                    <button
                      onClick={() => onReprocess(v.slug)}
                      disabled={!!reprocessing[v.slug]}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 px-2 py-1.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-wait"
                      data-testid={`admin-reprocess-${v.slug}`}
                      title="Mobile-Version neu erzeugen (bessere Qualität)"
                    >
                      {reprocessing[v.slug] ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                      Neu verarbeiten
                    </button>
                  )}
                  {!v.is_edited &&
                   v.mobile_transcode_status !== "paused_awaiting_fix" &&
                   (v.processing_status === "pending" || v.processing_status === "processing") && (
                    <button
                      onClick={() => onPauseTranscode(v.slug)}
                      disabled={!!pausing[v.slug]}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-red-300 hover:text-red-200 hover:bg-red-500/10 px-2 py-1.5 rounded-md transition-colors disabled:opacity-60 disabled:cursor-wait"
                      data-testid={`admin-pause-transcode-${v.slug}`}
                      title="Notbremse: Verarbeitung stoppen (Retry-Schleife brechen)"
                    >
                      {pausing[v.slug] ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Pause size={14} />
                      )}
                      Notbremse
                    </button>
                  )}
                  <button
                    onClick={() => setConfirming(v)}
                    className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2 py-1.5 rounded-md transition-colors"
                    data-testid={`admin-delete-${v.slug}`}
                    title="Löschen"
                  >
                    <Trash2 size={14} />
                    Löschen
                  </button>
                </div>
              </div>
            </article>
            );
          })}
        </div>
          </div>{/* /flex-1 min-w-0 */}
        </div>{/* /flex layout */}
      </main>

      <MoveToFolderModal
        open={moveTarget != null}
        folders={folders}
        videoCount={moveTarget === "bulk" ? selectedSlugs.size : 1}
        currentFolderId={
          moveTarget && moveTarget !== "bulk" ? moveTarget.folder_id : null
        }
        onClose={() => setMoveTarget(null)}
        onMove={(folderId) => (moveTarget === "bulk" ? onMoveBulk(folderId) : onMoveSingle(folderId))}
        onCreateFolder={onCreateFolder}
      />

      <EditModal
        video={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
      <ConfirmDialog
        open={!!confirming}
        title={`Video löschen?`}
        body={
          confirming
            ? `„${confirming.title}" wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`
            : ""
        }
        confirmLabel="Endgültig löschen"
        destructive
        onCancel={() => setConfirming(null)}
        onConfirm={onConfirmDelete}
      />
      <ConfirmDialog
        open={reprocessAllOpen}
        title="Alle Videos neu verarbeiten?"
        body={
          `Möchtest du wirklich alle ${originals.length} ${
            originals.length === 1 ? "Video" : "Videos"
          } in der Bibliothek neu verarbeiten? Das kann einige Minuten bis Stunden dauern, je nach Anzahl. Bestehende Videos sind währenddessen weiter abrufbar.`
        }
        confirmLabel={reprocessAllBusy ? "Wird gestartet…" : "Alle neu verarbeiten"}
        onCancel={() => (reprocessAllBusy ? null : setReprocessAllOpen(false))}
        onConfirm={onReprocessAll}
        confirmDisabled={reprocessAllBusy}
        confirmTestId="library-reprocess-all-confirm-btn"
        modalTestId="library-reprocess-all-confirm-modal"
      />
      <ManualCopyModal url={manualCopyUrl} onClose={() => setManualCopyUrl("")} />
    </div>
  );
}
